import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Scales as Scale,
  CircleNotch as Loader2,
  ShieldCheck,
  CalendarBlank as Calendar,
  Gavel,
  ArrowLeft,
  CheckCircle,
} from '@phosphor-icons/react';
import { api, getApiErrorMessage } from '@/lib/api';
import { isValidEmail, EMAIL_ERROR } from '@/lib/validators';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [sent, setSent] = useState(false);

  // Live email validation — runs on blur and (once touched) while typing.
  const validateEmail = (value: string) =>
    setEmailError(isValidEmail(value) ? '' : EMAIL_ERROR);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!isValidEmail(email)) {
      setEmailError(EMAIL_ERROR);
      return;
    }
    setEmailError('');
    setSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to send reset link. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
        {/* Left — dark brand panel */}
        <div className="hidden flex-col justify-between bg-ink-900 p-12 text-ink-200 lg:flex">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-600 text-white">
              <Scale className="h-5 w-5" weight="duotone" />
            </div>
            <div className="text-lg font-semibold tracking-tight text-white">Jurivoxa</div>
          </Link>

          <div className="max-w-lg">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-brand-400">
              For barristers, by design
            </div>
            <h1 className="mb-5 text-[34px] font-semibold leading-[1.15] tracking-tight text-white xl:text-[40px]">
              The operating system for the modern barrister.
            </h1>
            <p className="max-w-md text-[15px] leading-relaxed text-ink-300">
              Run your practice yourself — every matter, hearing, judgement, fee
              and document at your fingertips.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-ink-800 pt-8">
              {[
                { icon: <Gavel className="h-4 w-4" />, label: 'Hearings & lists' },
                { icon: <Calendar className="h-4 w-4" />, label: 'Diary & clashes' },
                { icon: <ShieldCheck className="h-4 w-4" />, label: 'BSB compliant' },
              ].map((f) => (
                <div key={f.label} className="flex flex-col gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-ink-800 text-brand-400">
                    {f.icon}
                  </div>
                  <div className="text-xs text-ink-300">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-ink-500">
            © {new Date().getFullYear()} Jurivoxa · Built for the Bar
          </div>
        </div>

        {/* Right — form */}
        <div className="flex items-center justify-center p-6 sm:p-12">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-600 text-white">
                <Scale className="h-5 w-5" weight="duotone" />
              </div>
              <div className="text-lg font-semibold tracking-tight text-foreground">Jurivoxa</div>
            </div>

            {sent ? (
              /* ── Success state ── */
              <div className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-500/10">
                    <CheckCircle className="h-7 w-7 text-emerald-600 dark:text-emerald-400" weight="fill" />
                  </div>
                </div>
                <h2 className="mb-2 text-[24px] font-semibold tracking-tight text-foreground">
                  Check your inbox
                </h2>
                <p className="text-sm text-muted-foreground">
                  If an account exists for that email, a reset link has been sent.
                  Check your inbox — and your spam folder if you do not see it within a few minutes.
                </p>
                <Link
                  to="/login"
                  className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to sign in
                </Link>
              </div>
            ) : (
              /* ── Form state ── */
              <>
                <div className="mb-6">
                  <h2 className="mb-1 text-[24px] font-semibold tracking-tight text-foreground">
                    Reset your password
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Enter your email address and we will send you a reset link.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-1.5 block text-sm font-medium text-foreground"
                    >
                      Email address
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      maxLength={255}
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (emailTouched) validateEmail(e.target.value); }}
                      onBlur={(e) => { setEmailTouched(true); validateEmail(e.target.value); }}
                      className="h-11 w-full rounded-md border border-border bg-card px-3.5 text-sm text-foreground transition placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                      placeholder="you@chambers.co.uk"
                    />
                    {emailError && <p className="mt-1.5 text-sm text-red-700 dark:text-red-300">{emailError}</p>}
                  </div>

                  {error && (
                    <p className="rounded-md bg-red-50 dark:bg-red-500/10 px-3 py-2.5 text-sm text-red-700 dark:text-red-300">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand-600 px-5 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      'Send reset link'
                    )}
                  </button>
                </form>

                <div className="mt-6">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to sign in
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
